import {
  PanelsTopLeft,
  Newspaper,
  Projector,
  BarChart3,
  GraduationCap,
  BookOpen,
  Users,
  Settings,
  Clock,
  CheckSquare,
  FileBarChart,
  UsersRound,
  TrendingUp,
  Calendar,
  Home,
  KeyRound,
  CheckCircle,
  Building,
  Zap,
  HardDrive,
  Shield,
} from "lucide-react";

export interface MenuItem {
  path: string;
  label: string;
  icon: JSX.Element;
  access?: 'all' | 'admin' | 'superadmin' | 'team_head' | 'management' | 'management_or_analytics' | 'leave_hr' | 'org_analytics' | 'content_mgmt' | 'leave_approval';
}

export interface MenuGroup {
  title: string;
  items: MenuItem[];
  defaultExpanded?: boolean;
}

export const menuGroups: MenuGroup[] = [
  {
    title: "Main",
    items: [
      { path: "/", label: "Dashboard", icon: <PanelsTopLeft className="w-5 h-5" /> },
      { path: "/manager-dashboard", label: "Command Center", icon: <Zap className="w-5 h-5" />, access: 'management' },
    ],
    defaultExpanded: true,
  },
  {
    title: "Work",
    items: [
      { path: "/projects", label: "Projects", icon: <Projector className="w-5 h-5" /> },
      { path: "/my-tasks", label: "My Tasks", icon: <CheckSquare className="w-5 h-5" /> },
      { path: "/timesheet", label: "Timesheet", icon: <Clock className="w-5 h-5" /> },
      { path: "/leave", label: "My Leave", icon: <Calendar className="w-5 h-5" /> },
      { path: "/work-location", label: "Work Location", icon: <Home className="w-5 h-5" /> },
      { path: "/request-access", label: "Request Access", icon: <KeyRound className="w-5 h-5" /> },
    ],
    defaultExpanded: true,
  },
  {
    title: "Management",
    items: [
      { path: "/people-teams", label: "People & Teams", icon: <UsersRound className="w-5 h-5" />, access: 'management_or_analytics' },
      { path: "/resources", label: "Resource Allocation", icon: <UsersRound className="w-5 h-5" />, access: 'management_or_analytics' },
      { path: "/tasks", label: "Tasks", icon: <CheckSquare className="w-5 h-5" /> },
      { path: "/leave-approvals", label: "Leave Approvals", icon: <CheckCircle className="w-5 h-5" />, access: 'leave_approval' },
      { path: "/clients", label: "Clients", icon: <Building className="w-5 h-5" />, access: 'admin' },
    ],
  },
  {
    title: "Analytics",
    items: [
      { path: "/analytics", label: "Analytics Hub", icon: <BarChart3 className="w-5 h-5" />, access: 'management_or_analytics' },
      { path: "/effort-report", label: "Effort Reports", icon: <FileBarChart className="w-5 h-5" />, access: 'management_or_analytics' },
      { path: "/analytics/team/utilization", label: "Team Utilization", icon: <TrendingUp className="w-5 h-5" />, access: 'management_or_analytics' },
      { path: "/analytics/admin/utilization", label: "Org Utilization", icon: <BarChart3 className="w-5 h-5" />, access: 'org_analytics' },
    ],
    defaultExpanded: false,

  },
  {
    title: "Team & Learning",
    items: [
      { path: "/directory", label: "People Directory", icon: <Users className="w-5 h-5" /> },
      { path: "/news", label: "News", icon: <Newspaper className="w-5 h-5" /> },
      { path: "/training", label: "Training", icon: <GraduationCap className="w-5 h-5" /> },
      { path: "/knowledge", label: "Knowledge Base", icon: <BookOpen className="w-5 h-5" /> },
      { path: "/drive", label: "Document Drive", icon: <HardDrive className="w-5 h-5" /> },
      { path: "/policies", label: "Policies", icon: <Shield className="w-5 h-5" /> },
      { path: "/surveys", label: "Surveys", icon: <BarChart3 className="w-5 h-5" /> },
    ],
    defaultExpanded: false,
  },
  {
    title: "Admin",
    items: [
      { path: "/admin", label: "Admin & Tools", icon: <Settings className="w-5 h-5" />, access: 'admin' },
      { path: "/admin/leave", label: "Leave Settings", icon: <Calendar className="w-5 h-5" />, access: 'leave_hr' },
      { path: "/admin/ai-analytics", label: "AI Analytics", icon: <BarChart3 className="w-5 h-5" />, access: 'superadmin' },
    ],
    defaultExpanded: false,
  },
];

export interface AccessContext {
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isTeamHead: boolean;
  isTeamLead: boolean;
  canManageLeaveHr: boolean;
  canViewOrgAnalytics: boolean;
  canManageContent: boolean;
}

export function canAccessItem(item: MenuItem, ctx: AccessContext): boolean;
export function canAccessItem(item: MenuItem, isAdmin: boolean, isTeamHead: boolean): boolean;
export function canAccessItem(
  item: MenuItem,
  ctxOrAdmin: AccessContext | boolean,
  isTeamHeadArg?: boolean
): boolean {
  // Support both call signatures
  const ctx: AccessContext = typeof ctxOrAdmin === 'boolean'
    ? {
        isAdmin: ctxOrAdmin,
        isSuperAdmin: false,
        isTeamHead: isTeamHeadArg ?? false,
        isTeamLead: false,
        canManageLeaveHr: ctxOrAdmin, // admin gets all
        canViewOrgAnalytics: ctxOrAdmin,
        canManageContent: ctxOrAdmin,
      }
    : ctxOrAdmin;

  if (!item.access) return true;

  switch (item.access) {
    case 'all':
      return true;
    case 'admin':
      return ctx.isAdmin;
    case 'superadmin':
      return ctx.isSuperAdmin;
    case 'team_head':
      return ctx.isAdmin || ctx.isTeamHead;
    case 'management':
      return ctx.isAdmin || ctx.isTeamHead || ctx.isTeamLead;
    case 'management_or_analytics':
      return ctx.isAdmin || ctx.isTeamHead || ctx.isTeamLead || ctx.canViewOrgAnalytics;
    case 'leave_hr':
      return ctx.isAdmin || ctx.canManageLeaveHr;
    case 'org_analytics':
      return ctx.isAdmin || ctx.canViewOrgAnalytics;
    case 'content_mgmt':
      return ctx.isAdmin || ctx.canManageContent;
    case 'leave_approval':
      return ctx.isAdmin || ctx.canManageLeaveHr || ctx.isTeamHead;
    default:
      return true;
  }
}
